import { ContactForm } from "../components/ContactForm";
import { FieldEvidenceMap, IntelligenceLoop } from "../components/Visuals";
import {
  comparisonCards,
  evidenceAccessMethods,
  intelligenceJobs,
  waterColumns,
} from "../content";

export function HomePage() {
  return (
    <main id="main-content">
      <section className="hero hero--memory" aria-labelledby="hero-title">
        <div className="hero__grid-pattern" aria-hidden="true" />
        <div className="shell memory-hero">
          <div className="memory-hero__copy">
            <p className="eyebrow">
              Frontline intelligence for the physical economy
            </p>
            <h1 id="hero-title">
              <span>Evidence from the field.</span>
              <span>Intelligence for real-world decisions</span>
            </h1>
            <p className="memory-hero__description">
              Kratis brings together hard-to-get operational evidence—from labs,
              inspectors, sensors, contractors, and other gated sources—into
              frontline intelligence for high-stakes decisions.
            </p>
            <p className="memory-hero__product">
              <span aria-hidden="true">01</span>
              First product: Wastewater Cost Increase Review
            </p>
            <div className="button-row">
              <a className="button" href="#water">
                Review a cost increase
                <span aria-hidden="true">↓</span>
              </a>
              <a className="button button--secondary" href="#edge">
                How Kratis works
                <span aria-hidden="true">↓</span>
              </a>
            </div>
          </div>
          <div className="memory-hero__visual">
            <FieldEvidenceMap />
          </div>
        </div>
      </section>

      <section className="section section--water water-v2" id="water">
        <div className="shell">
          <header className="water-v2__heading water-v2__heading--product">
            <p className="eyebrow">First product</p>
            <h2>
              <span>Wastewater costs went up</span>
              <span>Find out why before you keep paying</span>
              <span>or buy a fix</span>
            </h2>
            <p>
              Kratis independently checks how the charge was calculated, whether
              the samples reflect normal operations, and what changed in the
              plant. Management receives the least expensive justified next
              move—and one follow-up check of the result.
            </p>
          </header>

          <div className="water-product-header">
            <div>
              <p>Paid fixed-scope review</p>
              <h3>Wastewater Cost Increase Review</h3>
            </div>
            <p>
              One facility. One costly increase. One clear management decision
              in about ten business days after complete records arrive.
            </p>
          </div>

          <div className="water-fit-strip" aria-label="Typical customer fit">
            <strong>Typical fit</strong>
            <span>Food or beverage facility</span>
            <span>Wastewater sent to a municipal sewer</span>
            <span>
              $100K+ annualized increase or $500K+ annual wastewater spend
            </span>
          </div>

          <div className="water-grid water-grid--compact">
            {waterColumns.map((column, index) => (
              <article
                className={`water-card water-card--${index + 1}`}
                key={column.title}
              >
                <div className="water-card__heading">
                  <span>{column.number}</span>
                  <h3>{column.title}</h3>
                </div>
                <ul>
                  {column.items.map((item) => (
                    <li key={item}>
                      <span aria-hidden="true">{index === 1 ? "✓" : "—"}</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          <div className="water-example water-example--v2">
            <div className="water-example__question">
              <p>Illustrative example—not a customer result</p>
              <h3>Why did this facility’s wastewater costs increase?</h3>
            </div>
            <div>
              <span>Finding</span>
              <p>
                The increase appears to come from higher pollutant
                concentration—not more water use.
              </p>
            </div>
            <div>
              <span>Management decision</span>
              <p>Repeat a controlled sample before buying major equipment.</p>
            </div>
            <div>
              <span>Verification</span>
              <p>
                Compare the next three controlled samples with the current
                concentration and cost baseline.
              </p>
            </div>
            <footer>
              <span>Fact</span>
              <span>Calculation</span>
              <span>Inference</span>
              <span>Recommendation</span>
            </footer>
          </div>

          <a className="button water-cta" href="#contact">
            Review a Cost Increase
            <span aria-hidden="true">↗</span>
          </a>
        </div>
      </section>

      <section className="section section--edge edge-v2" id="edge">
        <div className="shell">
          <header className="edge-v2__heading">
            <p className="eyebrow">The Kratis edge</p>
            <h2>
              The facts that change a decision often live at the frontline
            </h2>
            <p>
              Other tools analyze the information already connected. Vendors
              recommend what they sell. Kratis goes after the missing fact,
              connects it to the decision, and stays through the result.
            </p>
          </header>

          <div className="edge-contrast" aria-label="How Kratis differs">
            {comparisonCards.map((card) => (
              <article
                className={card.highlighted ? "is-highlighted" : ""}
                key={card.title}
              >
                <span>{card.number}</span>
                <h3>{card.title}</h3>
                <p>{card.description}</p>
              </article>
            ))}
          </div>

          <div className="source-access">
            <div className="source-access__copy">
              <p className="eyebrow">How Kratis gets closer to the source</p>
              <h3>Hard-to-get does not have to mean out of reach</h3>
              <p>
                Today, Kratis starts with customer-approved records and arranges
                new measurements when needed. As the system grows, it may
                license, partner with, or selectively own a measurement source
                when that clearly improves the answer.
              </p>
            </div>
            <ul>
              {evidenceAccessMethods.map((method, index) => (
                <li key={method}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  {method}
                </li>
              ))}
            </ul>
          </div>

          <div className="independence-boundary">
            <strong>Independent by design</strong>
            <p>
              Kratis does not sell the treatment, repair, equipment, or
              operating service being judged. If it later owns a measurement
              source, that source remains separate from the work being
              evaluated. Rights, confidence, and commercial ties stay visible.
              The customer makes the final call.
            </p>
          </div>
        </div>
      </section>

      <section
        className="section section--intelligence"
        id="intelligence"
        aria-labelledby="intelligence-title"
      >
        <div className="shell">
          <header className="intelligence-heading">
            <p className="eyebrow">The intelligence engine</p>
            <h2 id="intelligence-title">
              Kratis is building focused models from hard-to-get evidence
            </h2>
            <p>
              With permission, Kratis connects frontline observations to the
              decision made, the action taken, and the verified result. That
              growing record can train focused industry models on evidence that
              public and widely sold datasets usually miss.
            </p>
          </header>

          <IntelligenceLoop />

          <div className="intelligence-job-grid">
            {intelligenceJobs.map((job) => (
              <article key={job.title}>
                <span>{job.number}</span>
                <h3>{job.title}</h3>
                <p>{job.description}</p>
              </article>
            ))}
          </div>

          <div className="intelligence-truth">
            <div>
              <p className="eyebrow">How it works today</p>
              <h3>Models assist. Evidence governs.</h3>
            </div>
            <p>
              Each Decision Review pairs software with qualified domain review.
              As permissioned decision-and-result records grow, Kratis will use
              them to build and improve focused industry models. Every important
              recommendation remains source-linked, human-reviewed, and under
              customer control.
            </p>
          </div>

          <div className="source-economics">
            <div>
              <p className="eyebrow">Why the evidence stream can last</p>
              <h3>A frontline source creates value twice</h3>
            </div>
            <div>
              <p>
                Labs, inspectors, sensors, and field contractors already earn
                money by doing useful work. That revenue helps sustain the
                evidence stream. Where rights permit, the resulting observations
                can also improve Kratis decisions and help build focused models.
              </p>
              <strong>
                Kratis may partner with, license from, or selectively own the
                source. It does not need to own the customer’s operating
                business.
              </strong>
            </div>
          </div>

          <div className="rights-strip">
            <p>
              Raw customer records are not sold. Cross-customer learning occurs
              only where contracts permit it.
            </p>
            <strong>
              Water is the first proof. The same engine can support other
              physical industries where evidence is hard to get and results can
              be measured.
            </strong>
          </div>
        </div>
      </section>

      <section className="section section--contact" id="contact">
        <div className="contact-ring contact-ring--one" aria-hidden="true" />
        <div className="contact-ring contact-ring--two" aria-hidden="true" />
        <div className="shell contact-grid contact-grid--memory">
          <div className="contact-copy">
            <p className="eyebrow">Start with the increase</p>
            <h2>Show Kratis what changed</h2>
            <p>
              Tell us when the wastewater cost increased, about how much is at
              issue, and what explanations or proposed fixes you have received.
              Do not send facility records yet.
            </p>
            <a className="text-link" href="#water">
              See the Cost Increase Review
              <span aria-hidden="true">↑</span>
            </a>
          </div>
          <div id="decision-form" className="contact-form-wrap">
            <div className="contact-form-wrap__header">
              <p>Request a Cost Increase Review</p>
              <span>Initial inquiry</span>
            </div>
            <ContactForm />
          </div>
        </div>
      </section>
    </main>
  );
}
